import { assign, cloneDeep, dropRight, flow, isArray, last } from 'lodash/fp';
import { IPayloadAction, ScannerActions } from './scanner.actions';
import { IBatchEvent } from '../interfaces';

export function batchStatusReducer(state: IBatchEvent[][], action: IPayloadAction) {

  switch (action.type) {
    case ScannerActions.RESET_SCANS:
      return [];

    case ScannerActions.SCANNER_INITIALIZED:
      return onScannerInitialized(state);

    case ScannerActions.ACQUIRE_IMAGES:
      return onAcquireImages(state);

    case ScannerActions.IMAGES_ACQUIRED:
      return onImagesAcquired(state);

    case ScannerActions.SCAN_FAILED:
      return onFailedToScan(state, action);

    case ScannerActions.EXTRACT_IMAGES:
      return onExtractImages(state);

    case ScannerActions.IMAGES_EXTRACTED:
      return onImagesExtracted(state);

    case ScannerActions.NOTHING_TO_SCAN:
      return onNothingToScan(state);

    case ScannerActions.SEND_IMAGES_BATCH:
      return onSendImagesBatch(state);

    case ScannerActions.IMAGES_BATCH_SENT:
      return onImagesBatchSent(state);

    case ScannerActions.SEND_IMAGES_BATCH_ERROR:
      return onSendImagesBatchError(state, action);

    default:
      return state;

  }
}

function onScannerInitialized(state) {
  if (state.length === 0) {
    // User has just started a session and needs a new empty batch to
    // receive image scans.
    return addNewBatch(state);
  }
  // User probably navigated back to /scans page from /review page,
  // so the session already has one or more batches, plus an empty
  // one waiting for more image scans.
  return state;

}

function onAcquireImages(state) {
  return addStatus({
    status: 'scan-started',
    text: 'Scanning Documents',
    completed: false,
    failed: false,
  }, state);
}

function onImagesAcquired(state) {
  return updateLastStatus('scan-started', { completed: true }, state);
}

function onFailedToScan(state, action) {

  const updatedState =
    updateLastStatus(['scan-started', 'encoding-started'], { completed: true, failed: true }, state);

  const newStatus = {
    status: 'failed-to-scan',
    text: `Failed to scan documents. ${action.payload.message}`,
    completed: true,
    failed: true,
  };
  const newStatusState = addStatus(newStatus, updatedState);
  return addNewBatch(newStatusState);
}

function onExtractImages(state) {
  return addStatus({
    status: 'encoding-started',
    text: 'Encoding images',
    completed: false,
    failed: false,
  }, state);

}

function onImagesExtracted(state) {
  return updateLastStatus('encoding-started', { completed: true }, state);
}

function onNothingToScan(state) {

  const updatedState =
    updateLastStatus(['scan-started', 'encoding-started'], { completed: true, failed: true }, state);

  const newStatus = {
    status: 'nothing-to-scan',
    text: 'There were no documents present in the scanner. Please check your scanner\'s auto-feeder and try again.',
    completed: true,
    failed: true,
  };
  const newStatusState = addStatus(newStatus, updatedState);
  return addNewBatch(newStatusState);
}

function onSendImagesBatch(state) {
  return addStatus({
    status: 'send-started',
    text: 'Saving images over the network',
    completed: false,
    failed: false,
  }, state);
}


function onSendImagesBatchError(state, action) {

  const updatedState =
    updateLastStatus('send-started', {completed: true, failed: true }, state);

  const newStatus = {
    status: 'error-sending-images-batch',
    text: 'There was a network error and the scanned images could not be saved.',
    details: action.payload.message,
    actions: [{label: 'Try Sending Again', dispatch: 'SEND_IMAGES_BATCH'}],
    completed: true,
    failed: true,
  };

  return addStatus(newStatus, updatedState);
}

function onImagesBatchSent(state) {
  const newStatusState = updateLastStatus('send-started', { completed: true, failed: false}, state);
  return addNewBatch(newStatusState);
}

export function updateLastStatus(expectedLast, updates, state) {
  const currentBatch: any = last(state) || [];
  const previousBatches = flow(dropRight(1), cloneDeep)(state);

  const lastStatus: any = last(currentBatch);
  const previousStatuses = flow(dropRight(1), cloneDeep)(currentBatch);

  if (!lastStatus) {
    return state;
  }
  if (!lastStatusWas(lastStatus.status, expectedLast)) {
    throw new Error('Something happened in an invalid order. ' +
      `Expected ${lastStatus.status} to be ${expectedLast}.`);
  }

  return [
    ...previousBatches,
    [
      ...previousStatuses,
      assign(lastStatus, updates),
    ],
  ];
}

function lastStatusWas(lastStatus, expectedLast) {
  if (isArray(expectedLast)) {
    return expectedLast.indexOf(lastStatus) !== -1;
  }
  return expectedLast === lastStatus;

}

export function addStatus(newStatus: IBatchEvent, state) {

  const currentBatch: any = last(state) || [];
  const previousBatches = flow(dropRight(1), cloneDeep)(state);

  return [ ...previousBatches, [ ...currentBatch, newStatus ]];

}

export function addNewBatch(state) {
  return [ ...state, [] ];
}
